<template>
  <!--
    ログイン画面でパスキーやGoogleを試して入れなかった人に、
    **入った直後、この場で登録させる。**

    文章で「セキュリティ設定から登録してください」と言うだけでは、
    入ったあと自分で探すことになる。押した理由も、そのころには忘れて
    いる。「押したのに設定できない」という声はそこから来ている。

    出るのは、その人が実際に登録できるときだけ。会社が閉じている方法を
    出すと、押しても必ず断られる。
  -->
  <!--
    **縦の中央に出す（centered）。**

    付けないと画面の上のほうに出て、見出しが固定ヘッダの下に潜る。
    2026-09-19 に手元の画面で実際にそうなった。文字は DOM にあるのに
    見えないので、読む人には**見出しの無いダイアログ**に見える。
  -->
  <CModal
    :show="open"
    :title="title"
    :close-on-backdrop="false"
    centered
    class="sign-in-setup"
    @update:show="closeRequested"
  >
    <!-- ============ 入口の説明と、登録の操作 ============ -->
    <template v-if="stage === 'ask'">
      <p class="sign-in-setup__lead">{{ entranceText.lead }}</p>
      <p class="sign-in-setup__note">{{ entranceText.why }}</p>

      <div v-if="entrance === 'passkey'" class="sign-in-setup__field">
        <label class="sign-in-setup__label" for="sign-in-setup-label">
          この端末の名前（任意）
        </label>
        <CInput
          id="sign-in-setup-label"
          v-model="label"
          type="text"
          maxlength="60"
          placeholder="例: 会社のノートPC"
        />
        <p class="sign-in-setup__hint">
          登録すると、次回からこの端末の指紋・顔・PIN でログインできます。
        </p>
      </div>

      <p v-else class="sign-in-setup__hint">{{ entranceText.leaveNote }}</p>
    </template>

    <!-- ============ 端末の確認を待っているところ ============ -->
    <div v-else-if="stage === 'working'" class="sign-in-setup__working">
      <CSpinner color="primary" size="sm"/>
      <span>{{ workingText }}</span>
    </div>

    <!-- ============ 登録できた ============ -->
    <template v-else-if="stage === 'done'">
      <CAlert color="success" class="sign-in-setup__result">
        {{ doneText }}
      </CAlert>
    </template>

    <!--
      ============ 途中でやめた ============

      **失敗として見せない。**端末の確認画面を閉じただけで、何も
      壊れていない。赤で出すと、押し間違えて閉じた人ほど、もう一度
      押さなくなる。
    -->
    <template v-else-if="stage === 'givenup'">
      <CAlert color="info" class="sign-in-setup__result">
        登録を中断しました。あとからでも登録できます。
      </CAlert>
    </template>

    <!-- ============ 登録できなかった ============ -->
    <template v-else>
      <CAlert color="danger" class="sign-in-setup__result">
        {{ failure }}
      </CAlert>
    </template>

    <p class="sign-in-setup__later">
      あとで登録するときは、画面右上のご自身の名前から
      「{{ SECURITY_SCREEN_NAME }}」を開いてください。
    </p>

    <template #footer>
      <div class="sign-in-setup__footer">
        <CButton
          v-if="stage === 'ask'"
          color="primary"
          :disabled="busy"
          @click="start"
        >{{ startButtonText }}</CButton>

        <CButton
          v-if="stage === 'givenup' || stage === 'failed'"
          color="primary"
          :disabled="busy"
          @click="retry"
        >もう一度試す</CButton>

        <CButton
          color="secondary"
          :disabled="stage === 'working'"
          @click="close"
        >{{ stage === 'done' ? '閉じる' : 'あとで' }}</CButton>
      </div>
    </template>
  </CModal>
</template>

<script>
import { SECURITY_SCREEN_NAME } from '@/common/screenNames'
import {
  isPasskeySupported,
  fetchPasskeyRegistrable,
  registerPasskey,
  listPasskeys
} from '@/services/passkey'
import {
  fetchLinkableProviders,
  listExternalIdentities,
  isNetworkFailure
} from '@/services/externalIdpLogin'
import { startGoogleLink } from '@/services/googleLogin'
import { startMicrosoftLink } from '@/services/microsoftLogin'
import {
  isPasskeyGivenUp,
  isPasskeyAlreadyOnThisDevice,
  describePasskeyRegisterFailure
} from '@/common/passkeyRegisterFailure'

/** ログイン画面が覚えておいた入口の置き場。 */
const KEY = 'preika.triedEntrance'

/**
 * 印が効く長さ（ミリ秒）。
 *
 * 押してからメールとパスワードで入るまでの間。これを過ぎたものは、
 * **同じタブを次に使う人のものかもしれない**ので使わない。
 */
const MAX_AGE = 5 * 60 * 1000

/**
 * 入口ごとの言い方と、結び付けを始める口。
 *
 * **入口の名前をそのまま出さない。**「passkey」「google」はこちらの
 * 呼び名で、読む人の言葉ではない。
 */
const ENTRANCES = {
  passkey: {
    label: 'パスキー',
    title: 'パスキーを登録しますか',
    /*
     * **「先ほど、あなたが押した」と決めつけない。**押したのが誰かは
     * ログイン前なので分からず、共有端末では前の人の印で出ることが
     * ある。**いま読んでいる人にとって事実であること**だけを書く。
     */
    lead: 'この端末では、まだパスキーでログインできません。',
    why: 'パスキーは、ログインしたあとに登録します。いま登録できます。',
    action: 'この端末を登録する',
    working: '画面の案内にしたがって、指紋・顔・PIN のいずれかで'
      + '確認してください…',
    done: '次回のログインから、この端末の指紋・顔・PIN でログインできます。'
  },
  google: {
    label: 'Google',
    title: 'Google アカウントと連携しますか',
    lead: 'Google アカウントとの連携が完了していません。',
    why: 'Google でログインするには事前連携が必要です。'
      + '今すぐ連携できます。',
    action: 'Google と連携する',
    working: 'Google の画面へ移動しています…',
    leaveNote: 'Google の認証画面へ移動します。'
      + '中断する場合は、ブラウザの「戻る」ボタンでお戻りください。',
    failedToStart: 'Google の画面へ移動できませんでした。時間をおいて再度お試しください。',
    startLink: startGoogleLink
  },
  microsoft: {
    label: 'Microsoft',
    title: 'Microsoft アカウントと連携しますか',
    lead: 'Microsoft アカウントとの連携が完了していません。',
    why: 'Microsoft でログインするには事前連携が必要です。'
      + '今すぐ連携できます。',
    action: 'Microsoft と連携する',
    working: 'Microsoft の画面へ移動しています…',
    leaveNote: 'Microsoft の認証画面へ移動します。'
      + '中断する場合は、ブラウザの「戻る」ボタンでお戻りください。',
    failedToStart: 'Microsoft の画面へ移動できませんでした。時間をおいて再度お試しください。',
    startLink: startMicrosoftLink
  }
}

export default {
  name: 'SignInMethodSetupDialog',

  data () {
    return {
      SECURITY_SCREEN_NAME,
      /** 'passkey' / 'google' / 'microsoft' のどれか。 */
      entrance: '',
      open: false,
      /** 'ask' / 'working' / 'done' / 'givenup' / 'failed' */
      stage: 'ask',
      label: '',
      failure: '',
      busy: false
    }
  },

  computed: {
    entranceText () {
      return ENTRANCES[this.entrance] || {}
    },

    title () {
      if (this.stage === 'done') return '登録できました'
      return this.entranceText.title || ''
    },

    startButtonText () {
      return this.entranceText.action || ''
    },

    workingText () {
      return this.entranceText.working || ''
    },

    doneText () {
      return this.entranceText.done ||
        this.entranceText.label + ' と連携しました。'
    }
  },

  async mounted () {
    const tried = this.readEntrance()

    /*
     * **読んだらすぐ消す。**
     *
     * 登録しないと決めた人にとって、毎回出るものは消えない広告に
     * なる。やり直しはこのダイアログの中でできる。
     */
    this.forget()

    if (!tried || !ENTRANCES[tried]) return
    this.entrance = tried

    /*
     * **その人が本当に登録できるときだけ開く。**
     *
     * 会社が閉じている方法を出すと、押しても必ず断られる。
     * 「言われたとおりにしたのに直らない」がいちばん困る。
     */
    if (await this.canRegister(tried)) {
      this.open = true
    }
  },

  created () {
    /*
     * **提供元から戻ってきたときのために見張る。**
     *
     * ブラウザが履歴キャッシュから戻すと、離れたときの状態がそのまま
     * 復元される（読み込み直しが走らない）。working のまま固まるので、
     * ここで立て直す。
     */
    this.onPageShow = this.recoverAfterComingBack
    window.addEventListener('pageshow', this.onPageShow)
  },

  beforeDestroy () {
    window.removeEventListener('pageshow', this.onPageShow)
  },

  methods: {
    readEntrance () {
      try {
        const 生 = window.sessionStorage.getItem(KEY)
        if (!生) return ''

        const 中 = JSON.parse(生)
        if (!中 || !中.kind) return ''

        /*
         * **古い印は使わない。**
         *
         * 同じタブを次に使う人が、前の人の「先ほどパスキーでは…」を
         * 見ることがある（共有端末）。押してからメールとパスワードで
         * 入るまでなら、5分に収まる。
         */
        if (!中.at || Date.now() - Number(中.at) > MAX_AGE) return ''

        return String(中.kind)
      } catch (error) {
        /*
         * 保存を切っている端末では読めない。読めた中身が JSON でない
         * ときも、ここへ来る（古い形が残っている場合）。**出ないだけ。**
         */
        return ''
      }
    },

    forget () {
      try {
        window.sessionStorage.removeItem(KEY)
      } catch (error) {
        /* 消せなくても害は無い。次のログインで上書きされる。 */
      }
    },

    async canRegister (entrance) {
      if (entrance === 'passkey') {
        /*
         * 端末が対応していることと、会社が許していることの両方。
         * **どちらか片方でも欠けたら出さない。**
         *
         * ここで /passkey/availability を見てはいけない。あれは
         * ログイン画面向けで会社の設定を含まないので、会社が閉じて
         * いる人にも出てしまう。押すとサーバが断る。
         * 2026-09-19 に実際にそうなった。
         */
        if (!isPasskeySupported()) return false
        const 可否 = await fetchPasskeyRegistrable()
        if (!可否.offered || !可否.allowedByCompany) return false

        /*
         * **すでにパスキーを持っている人には出さない。**
         *
         * 押したのが誰かはログイン前なので分からない。共有端末では、
         * 前の人が押した印でこの案内が出ることがある。ここで本人の
         * 持ち物を確かめておけば、出るのは**まだ1本も持っていない人**
         * だけになり、誰に出ても事実どおりの案内になる。
         *
         * 外部アカウント側には同じ確認が最初から入っていた
         * （listExternalIdentities）。パスキー側にだけ無かった。
         */
        try {
          const 持ち物 = await listPasskeys()
          return Array.isArray(持ち物) && 持ち物.length === 0
        } catch (error) {
          /* 聞けなければ出さない。押しても通らない案内を見せない。 */
          return false
        }
      }

      try {
        const providers = await fetchLinkableProviders()
        const one = providers && providers[entrance]

        /*
         * **offered と allowedByCompany の両方を見る。**
         *
         * 提供元ごとに {offered, allowedByCompany} が返る。行が
         * あるかどうかで判断すると、どちらも false の行でも通って
         * しまう（空でないものは真になる）。
         */
        if (!one || !one.offered || !one.allowedByCompany) return false

        /*
         * すでに結び付けてある人には出さない。結び付け直しはできず、
         * 押しても断られる。入れなかった理由はほかにある。
         */
        const linked = await listExternalIdentities()
        return !linked.some(item =>
          String(item.provider || '').toUpperCase() === entrance.toUpperCase())
      } catch (error) {
        return false
      }
    },

    start () {
      return this.entrance === 'passkey'
        ? this.registerHere()
        : this.sendToProvider()
    },

    retry () {
      this.failure = ''
      this.stage = 'ask'
    },

    /**
     * <b>この画面を離れずに、パスキーを1本作る。</b>
     *
     * <p>終わり方は3つあって、見せ方を分ける。作れた／本人が途中で
     * やめた／作れなかった。<b>やめたのを失敗として見せない。</b></p>
     */
    async registerHere () {
      if (this.busy) return
      this.busy = true
      this.stage = 'working'
      this.failure = ''
      try {
        await registerPasskey(this.label.trim())
        this.stage = 'done'
      } catch (error) {
        if (isPasskeyAlreadyOnThisDevice(error)) {
          /* やることは残っていない。失敗ではなく「済んでいる」。 */
          this.stage = 'done'
        } else if (isPasskeyGivenUp(error)) {
          this.stage = 'givenup'
        } else {
          this.failure = describePasskeyRegisterFailure(error)
          this.stage = 'failed'
        }
      } finally {
        this.busy = false
      }
    },

    /**
     * <b>提供元の画面へ送り出す。</b>
     *
     * <p>外部アカウントの結び付けは、この画面の中では終われない。
     * 戻り先はサーバが決める（{@code /#/my/security?link=...}）ので、
     * 成否はそこで出る。</p>
     *
     * <p>ここで見るのは<b>送り出せたかどうかだけ。</b>移動できなければ
     * この場でやり直せる。</p>
     */
    async sendToProvider () {
      if (this.busy) return
      this.busy = true
      this.stage = 'working'
      this.failure = ''
      try {
        const url = await this.entranceText.startLink()
        window.location.assign(url)
      } catch (error) {
        this.failure = isNetworkFailure(error)
          ? '通信できませんでした。接続を確認してからお試しください。'
          : this.entranceText.failedToStart
        this.stage = 'failed'
        this.busy = false
      }
    },

    /**
     * <b>閉じる操作を1か所で受ける。</b>
     *
     * <p>フッターのボタンだけを無効にしても、見出しの「×」は別の口
     * なので閉じられてしまう。閉じると画面からは消えるのに登録は
     * 進み、鍵ができても知らせが誰にも見えない。</p>
     */
    closeRequested (shown) {
      if (shown) { this.open = true; return }
      /* 端末の確認や移動を待っている最中は閉じない。 */
      if (this.stage === 'working') return
      this.open = false
    },

    /**
     * <b>提供元から戻ってきたら、立て直す。</b>
     *
     * <p>履歴キャッシュからの復元（{@code event.persisted}）のときだけ
     * 働く。ふつうの読み込みなら最初からやり直すので何もしなくてよい。</p>
     *
     * <p><b>無条件に「やり直せます」に戻さない。</b>向こうで結び付けを
     * 終えてから戻った人に、もう一度押させることになる。いまの状態を
     * サーバに聞いてから決める。</p>
     */
    async recoverAfterComingBack (event) {
      if (!event || !event.persisted) return
      if (this.stage !== 'working') return

      /* パスキーはこの画面を離れないので、ここへは来ない。 */
      if (this.entrance === 'passkey') {
        this.busy = false
        this.stage = 'ask'
        return
      }

      this.busy = false
      try {
        const linked = await listExternalIdentities()
        const 済み = linked.some(item =>
          String(item.provider || '').toUpperCase() === this.entrance.toUpperCase())
        this.stage = 済み ? 'done' : 'ask'
      } catch (error) {
        /* 聞けなければ、やり直せる側へ倒す。閉じ込めない。 */
        this.stage = 'ask'
      }
    },

    close () {
      this.open = false
    }
  }
}
</script>

<style scoped>
.sign-in-setup__lead {
  margin: 0 0 6px;
  font-weight: 700;
}

.sign-in-setup__note {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.8;
}

.sign-in-setup__field {
  margin-bottom: 4px;
}

.sign-in-setup__label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
}

.sign-in-setup__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--preika-gray-700);
}

.sign-in-setup__working {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  font-size: 13px;
}

.sign-in-setup__result {
  margin-bottom: 8px;
}

.sign-in-setup__later {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--preika-gray-700);
}

.sign-in-setup__footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
